import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Skeleton,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Warning,
  HelpOutline,
  Refresh,
  Memory,
  Timer,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { useProcessingHealth } from '../hooks/useSystem';

const formatUptime = (seconds: number): string => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);

  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};

interface ProcessingStatusCardProps {
  embedded?: boolean;
}

const ProcessingStatusCard: React.FC<ProcessingStatusCardProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { data: health, isLoading, error, refetch, isFetching } = useProcessingHealth();

  const getStatusIcon = () => {
    if (isLoading) return <CircularProgress size={20} />;
    if (error || health?.status === 'unhealthy') return <Error sx={{ color: '#ef4444' }} />;
    if (health?.status === 'degraded') return <Warning sx={{ color: '#f59e0b' }} />;
    if (health?.status === 'healthy') return <CheckCircle sx={{ color: '#10b981' }} />;
    return <HelpOutline sx={{ color: '#6b7280' }} />;
  };

  const getStatusColor = (): 'success' | 'warning' | 'error' | 'default' => {
    if (error || health?.status === 'unhealthy') return 'error';
    if (health?.status === 'degraded') return 'warning';
    if (health?.status === 'healthy') return 'success';
    return 'default';
  };

  const getStatusText = () => {
    if (isLoading) return 'Checking...';
    if (error) return 'Offline';
    if (health?.status === 'unknown') return 'Not Configured';
    return health?.status ? health.status.charAt(0).toUpperCase() + health.status.slice(1) : 'Unknown';
  };

  const content = (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Memory sx={{ color: '#8b5cf6' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Processing API
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={getStatusIcon()}
            label={getStatusText()}
            color={getStatusColor()}
            size="small"
          />
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                refetch();
              }}
              disabled={isFetching}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {isLoading ? (
        <Box>
          <Skeleton width="60%" height={24} />
          <Skeleton width="40%" height={20} />
        </Box>
      ) : error ? (
        <Typography variant="body2" color="error">
          Unable to connect to Processing API
        </Typography>
      ) : health?.status === 'unknown' ? (
        <Typography variant="body2" color="text.secondary">
          {health.message || 'Processing API not configured'}
        </Typography>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', gap: 3, mb: 1 }}>
            {health?.jobs_processed !== undefined && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {health.jobs_processed.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Processed
                </Typography>
              </Box>
            )}
            {health?.jobs_in_queue !== undefined && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {health.jobs_in_queue}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  In Queue
                </Typography>
              </Box>
            )}
          </Box>

          {health?.uptime_seconds !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Timer fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Uptime: {formatUptime(health.uptime_seconds)}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 1, textAlign: 'right' }}
      >
        Click for details
      </Typography>
    </>
  );

  if (embedded) {
    return (
      <Box
        sx={{ cursor: 'pointer' }}
        onClick={() => navigate('/system/status')}
      >
        {content}
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        p: 2,
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
      onClick={() => navigate('/system/status')}
    >
      {content}
    </Paper>
  );
};

export default ProcessingStatusCard;
